import _ from "lodash";
/** external */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { CardSelection } from "@toddle-edu/ds-web";
/** internal */
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import RadioButton from "UIComponents/RadioButton";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { InfoIcon } from "SvgComponents";
import { colors } from "Constants";
/** relative */
import classes from "./RadioButtonList.scss";
import { generateRandomId } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

/** customized RadioButtonList */
class RadioButtonList extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  updateInputField = value => {
    const { updateInputField, fieldKey } = this.props;

    updateInputField?.({
      params: { [fieldKey]: value },
      isMutation: true,
      isDebounce: false,
    });
  };

  getAllOption = () => {
    const {
      styles,
      options,
      value,
      disabled,
      itemSelectedStyle,
      buttonColor,
      name,
      labelStyle,
      allOptionLabel,
    } = this.props;

    let allOptionLabelLocale = allOptionLabel;

    if (allOptionLabelLocale === "common:all") {
      allOptionLabelLocale = this.props.t(allOptionLabelLocale);
    }

    const optionValues = _.map(options, ({ value }) => value);

    const isChecked = _.size(_.first(value)) === _.size(optionValues);

    return (
      <div
        className={classes.option}
        style={{
          ..._.get(styles, "listItemStyle", {}),
          ...(isChecked ? itemSelectedStyle : {}),
        }}
        onClick={disabled ? null : () => this.updateInputField(optionValues)}
        onKeyDown={
          disabled
            ? null
            : e =>
                onEnterOrSpaceKeyDown(e, () =>
                  this.updateInputField(optionValues)
                )
        }
        role={disabled ? undefined : "button"}
        tabIndex={disabled ? undefined : 0}
        data-test-id="worksheet-radioButtonList-allOptions"
      >
        <RadioButton
          label={allOptionLabelLocale}
          isChecked={isChecked}
          isDisabled={disabled}
          color={buttonColor}
          labelStyles={labelStyle}
          name={name}
          testId="worksheet-radioButtonList-allOptions"
        />
      </div>
    );
  };

  getOption = () => {
    const {
      styles,
      options,
      disabled,
      value,
      buttonColor,
      labelStyle,
      itemSelectedStyle,
      isOptionWithBorder,
    } = this.props;
    const listItem = classNames(
      { [classes.option]: true },
      { [classes.optionWithBorder]: isOptionWithBorder }
    );
    return _.map(options, (option, index) => {
      const { label, value: optionValue, subText } = option;
      const resolvedValue = _.first(value);
      const randomId = generateRandomId();
      return (
        <div
          key={index}
          className={listItem}
          style={{
            ..._.get(styles, "listItemStyle", {}),
            ...(resolvedValue?.correct === optionValue?.correct
              ? itemSelectedStyle
              : {}),
          }}
        >
          <div
            onClick={disabled ? null : () => this.updateInputField(optionValue)}
            onKeyDown={
              disabled
                ? null
                : e =>
                    onEnterOrSpaceKeyDown(e, () =>
                      this.updateInputField(optionValue)
                    )
            }
            role={disabled ? undefined : "radio"}
            aria-checked={resolvedValue?.correct == optionValue?.correct}
            tabIndex={disabled ? undefined : 0}
            data-test-id={`worksheet-radioButtonList-item-${_.camelCase(
              label
            )}`}
          >
            <RadioButton
              label={label}
              subText={subText}
              isChecked={resolvedValue?.correct == optionValue?.correct}
              isDisabled={disabled}
              color={buttonColor}
              labelStyles={labelStyle}
              name={randomId}
              tabIndex={-1}
              testId={`worksheet-radioButtonList-item-${_.camelCase(label)}`}
            />
          </div>
        </div>
      );
    });
  };

  getCardSelectionOptions = () => {
    const { options, styles, disabled, value } = this.props;

    return _.map(options, option => {
      //optionDisable field  is for when we want to disable particular option not whole option list
      const { label, value: optionValue, subText, optionDisable } = option;
      const suffixIcon = subText ? (
        <LinkWithTooltip
          tooltip={subText}
          placement={"bottom"}
          trigger={["hover"]}
          tooltipContainerStyle={{ width: 284 }}
        >
          <div className={classes.infoIcon}>
            <InfoIcon fill={colors.neutral400} width={14} height={14} />
          </div>
        </LinkWithTooltip>
      ) : null;

      return (
        <CardSelection
          title={label}
          type="radio"
          key={optionValue}
          value={optionValue}
          containerStyle={_.get(styles, "listItemStyle", {})}
          suffixIcon={suffixIcon}
          disabled={optionDisable ? optionDisable : disabled}
          selectedValues={value}
          onCardSelectionClick={() => this.updateInputField(optionValue)}
          testId={`worksheet-radioButtonList-${_.camelCase(label)}`}
        />
      );
    });
  };

  shouldShowEditEmpty = () => {
    return _.get(this.props.options, "length", 0) == 0;
  };

  renderView = () => {
    const { viewTextStyle } = this.props;
    return (
      <div className={classes.viewText} style={viewTextStyle}>
        {this.getViewValue()}
      </div>
    );
  };

  getViewValue = () => {
    const { options, value, emptyText } = this.props;

    return value
      ? _.get(_.find(options, { value: _.first(value) }), "label", "")
      : emptyText
      ? emptyText
      : "N/A";
  };

  renderEdit = () => {
    const { styles, useCardSelection } = this.props;

    return (
      <div className={classes.container}>
        <div
          className={classes.optionContainer}
          role="radiogroup"
          style={_.get(styles, "listContainerStyle", {})}
        >
          {useCardSelection ? this.getCardSelectionOptions() : this.getOption()}
        </div>
      </div>
    );
  };
}

export default RadioButtonList;

RadioButtonList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  buttonColor: "blue",
  labelStyle: {},
  itemSelectedStyle: {},
  viewTextStyle: {},
  useCardSelection: false,
};

RadioButtonList.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
};
